import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, ArrowRight } from "lucide-react";
import { TreadPattern } from "./TreadPattern";

const SPEC_COLS = [
  ["size", "Size"],
  ["base", "Base W (mm)"],
  ["depth", "Depth (mm)"],
  ["thk", "Base Thk (mm)"],
  ["length", "Length (in)"],
  ["weight", "Weight (kg)"],
  ["strips", "Strips"],
];

export const ProductCard = ({ product, index }) => {
  const [open, setOpen] = useState(false);
  const isTread = Boolean(product.variants);
  const sizes = isTread ? [...new Set(product.variants.map((x) => x.size))] : [];

  return (
    <motion.article
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, delay: (index % 3) * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group flex flex-col border border-white/10 bg-panel/60 hover:border-amber/60 transition-colors duration-300"
      data-testid={`product-card-${product.slug}`}
    >
      <div className="relative h-36 bg-ink blueprint-grid overflow-hidden">
        <TreadPattern
          type={product.pattern}
          className="h-full w-full opacity-80 group-hover:opacity-100 group-hover:scale-[1.03] transition-all duration-500"
        />
        <span className="absolute top-3 left-3 font-mono text-[9px] tracking-[0.3em] text-slate-500">
          PATTERN {String(index + 1).padStart(2, "0")}
        </span>
      </div>

      <div className="flex flex-col flex-1 p-5 sm:p-6">
        <h3 className="font-display text-2xl sm:text-3xl font-extrabold uppercase tracking-tight text-white">
          {product.code}
        </h3>

        {isTread ? (
          <>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {sizes.map((s) => (
                <span key={s} className="font-mono text-[10px] tracking-wider text-slate-300 border border-white/15 px-2 py-1">
                  {s}
                </span>
              ))}
            </div>

            <div className="mt-auto pt-6 flex items-center justify-between gap-3">
              <button
                onClick={() => setOpen(!open)}
                data-testid={`product-specs-toggle-${product.slug}`}
                className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400 hover:text-white transition-colors"
                aria-expanded={open}
              >
                <Plus size={13} className={`text-amber transition-transform duration-300 ${open ? "rotate-45" : ""}`} />
                Specifications
              </button>
              <Link
                to={`/contact?product=${encodeURIComponent(product.code)}`}
                data-testid={`product-request-quote-${product.slug}`}
                className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber hover:text-amberhover"
              >
                Quote <ArrowRight size={13} />
              </Link>
            </div>

            <AnimatePresence initial={false}>
              {open && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="overflow-hidden"
                  data-testid={`product-specs-${product.slug}`}
                >
                  <div className="mt-4 -mx-5 sm:-mx-6 overflow-x-auto">
                    <table className="w-full min-w-[560px] text-left border-t border-white/10">
                      <thead>
                        <tr className="font-mono text-[9px] uppercase tracking-[0.15em] text-slate-500">
                          {SPEC_COLS.map(([k, label]) => (
                            <th key={k} className="px-3 py-2 font-medium">{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="font-mono text-[11px] text-slate-200">
                        {product.variants.map((row, i) => (
                          <tr key={i} className="border-t border-white/5">
                            <td className="px-3 py-2 text-white">{row.size}</td>
                            <td className="px-3 py-2">{row.base}</td>
                            <td className="px-3 py-2">{row.depth.toFixed(2)}</td>
                            <td className="px-3 py-2">{row.thk.toFixed(2)}</td>
                            <td className="px-3 py-2">{row.length}</td>
                            <td className="px-3 py-2">{row.weight.toFixed(2)}</td>
                            <td className="px-3 py-2">{row.strips}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <>
            <p className="mt-3 text-sm text-slate-400 leading-relaxed">{product.desc}</p>
            <div className="mt-auto pt-6">
              <Link
                to={`/contact?product=${encodeURIComponent(product.code)}`}
                data-testid={`product-request-quote-${product.slug}`}
                className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-amber hover:text-amberhover"
              >
                Enquire <ArrowRight size={13} />
              </Link>
            </div>
          </>
        )}
      </div>
    </motion.article>
  );
};

export default ProductCard;
